import type { CreateInviteLinkInput, InviteLink } from '@nexo/types';
import { Button, IconButton, Input, Select, useToast } from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Link2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { formatLongDate } from '../../lib/format';
import { errorMessage } from '../../lib/forms';

const expiryOptions = [
  { value: '1', label: '1 día' },
  { value: '7', label: '7 días' },
  { value: '30', label: '30 días' },
  { value: 'never', label: 'Nunca' },
];
const usesOptions = [
  { value: 'unlimited', label: 'Sin límite' },
  { value: '1', label: '1 uso' },
  { value: '5', label: '5 usos' },
  { value: '10', label: '10 usos' },
  { value: '25', label: '25 usos' },
];

/** Shareable join links (admins only): anyone with the link can join the team. */
export function InviteLinks() {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [expiry, setExpiry] = useState('7');
  const [uses, setUses] = useState('unlimited');
  const { t } = useTranslation();
  const key = ['team', team.id, 'invite-links'];
  const links = useQuery({ queryKey: key, queryFn: () => api.inviteLinks.list(team.id) });

  const create = useMutation({
    mutationFn: () => {
      const input: CreateInviteLinkInput = {
        expiresInDays: expiry === 'never' ? null : (Number(expiry) as 1 | 7 | 30),
        maxUses: uses === 'unlimited' ? null : Number(uses),
      };
      return api.inviteLinks.create(team.id, input);
    },
    onSuccess: async (link) => {
      await navigator.clipboard.writeText(link.url).catch(() => undefined);
      toast({
        title: t('Enlace creado y copiado'),
        description: t('Compártelo con quien quieras invitar.'),
        tone: 'success',
      });
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) =>
      toast({
        title: t('No se pudo crear el enlace'),
        description: errorMessage(err),
        tone: 'error',
      }),
  });
  const revoke = useMutation({
    mutationFn: (linkId: string) => api.inviteLinks.revoke(team.id, linkId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  return (
    <section className="mb-5 rounded-xl border border-border bg-surface-2 p-4">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <Link2 className="size-4 text-accent" /> {t('Invitar con un enlace')}
      </h3>
      <p className="mb-3 text-xs text-muted">
        {t('Cualquiera que tenga el enlace y una cuenta podrá unirse al equipo como miembro.')}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label={t('Caduca en')}
          value={expiry}
          onChange={setExpiry}
          className="w-40!"
          options={expiryOptions.map((o) => ({
            value: o.value,
            label: t('Caduca: {{when}}', { when: t(o.label) }),
          }))}
        />
        <Select
          aria-label={t('Usos')}
          value={uses}
          onChange={setUses}
          className="w-36!"
          options={usesOptions.map((o) => ({ value: o.value, label: t(o.label) }))}
        />
        <Button
          icon={<Link2 className="size-4" />}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          {t('Crear enlace')}
        </Button>
      </div>
      {links.data && links.data.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {links.data.map((link) => (
            <LinkRow key={link.id} link={link} onRevoke={() => revoke.mutate(link.id)} />
          ))}
        </ul>
      )}
    </section>
  );
}

function LinkRow({ link, onRevoke }: { link: InviteLink; onRevoke: () => void }) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const details = [
    link.maxUses
      ? t('{{uses}}/{{max}} usos', { uses: link.uses, max: link.maxUses })
      : link.uses === 1
        ? t('1 uso')
        : t('{{count}} usos', { count: link.uses }),
    link.expiresAt
      ? t('caduca el {{date}}', { date: formatLongDate(link.expiresAt) })
      : t('no caduca'),
  ].join(' · ');
  return (
    <li className="rounded-lg bg-surface p-2.5 ring-1 ring-border">
      <div className="flex gap-2">
        <Input
          readOnly
          value={link.url}
          onFocus={(e) => e.target.select()}
          className="h-8 font-mono text-[11px]"
          aria-label={t('Enlace de invitación')}
        />
        <IconButton
          label={t('Copiar enlace')}
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(link.url).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? <Check className="text-success" /> : <Copy />}
        </IconButton>
        <IconButton
          label={t('Revocar enlace')}
          size="sm"
          className="hover:text-danger"
          onClick={() => {
            if (window.confirm(t('¿Revocar este enlace? Dejará de funcionar para quien lo tenga.')))
              onRevoke();
          }}
        >
          <Trash2 />
        </IconButton>
      </div>
      <p className="mt-1.5 px-1 text-[11px] text-muted">
        {details}
        {link.createdBy && ` · ${t('creado por {{name}}', { name: link.createdBy.displayName })}`}
      </p>
    </li>
  );
}
